import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../components/atlas/helpers";
import { Activity, Zap, ShieldAlert, Crosshair, TrendingUp, TrendingDown, Target, Wallet, Cpu, Network, Gauge, Globe2 } from "lucide-react";

export function WhaleHunterWorkspace() {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [localTime, setLocalTime] = useState(new Date());

  useEffect(() => {
    let alive = true;
    const fetchState = () => {
      axios.get(`${API}/whale-hunter/state`)
        .then(res => {
          if (alive) {
            setState(res.data);
            setError(null);
          }
        })
        .catch(err => {
          if (alive) {
            setError(err.message);
          }
        });
    };

    fetchState();
    const interval = setInterval(fetchState, 500); // 500ms for faster UI updates
    
    const timeInterval = setInterval(() => setLocalTime(new Date()), 1000);
    return () => {
      alive = false;
      clearInterval(interval);
      clearInterval(timeInterval);
    };
  }, []);

  if (!state && !error) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center bg-[#050505]">
        <div className="relative">
          <div className="absolute -inset-4 rounded-full border border-cyan-500/30 animate-[ping_2s_ease-in-out_infinite]"></div>
          <Activity className="w-12 h-12 text-cyan-500 animate-pulse relative z-10" />
        </div>
        <div className="mt-8 text-cyan-500/70 font-mono text-sm tracking-[0.3em] animate-pulse">
          INITIALIZING HFT NODE
        </div>
      </div>
    );
  }

  const pnl = state?.in_trade 
    ? (state.trade_dir === 1 
        ? ((state.live_bid - state.entry_price) / state.entry_price) 
        : ((state.entry_price - state.live_ask) / state.entry_price))
    : 0;

  const pnlUsd = state?.position_size_usd * pnl;
  const spread = Math.abs((state?.live_ask || 0) - (state?.live_bid || 0));
  const midPrice = ((state?.live_ask || 0) + (state?.live_bid || 0)) / 2;
  const winRate = state?.total_trades > 0 ? ((state?.wins / state?.total_trades) * 100).toFixed(1) : 0;
  
  const fNum = (val, dec = 2) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });

  return (
    <div className="h-full flex flex-col p-6 overflow-y-auto bg-[#030303] text-gray-100" style={{ backgroundImage: "radial-gradient(circle at 50% 0%, #0a1128 0%, #030303 100%)" }}>
      {/* Header */}
      <div className="flex items-center justify-between pb-6 border-b border-white/5 mb-6">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-white tracking-tight">
            <div className="bg-cyan-500/10 p-2 rounded-xl border border-cyan-500/30">
              <Crosshair className="w-6 h-6 text-cyan-400" />
            </div>
            Whale Hunter
            <span className="text-[10px] font-mono px-2 py-1 rounded bg-cyan-500/20 text-cyan-400 ml-2 uppercase tracking-widest border border-cyan-500/30 shadow-[0_0_15px_rgba(34,211,238,0.2)]">
              Live Execution Node
            </span>
          </h1>
          <p className="text-sm text-gray-400 mt-2 tracking-wide pl-14">
            Volume-Bar Microstructure Arbitrage on Binance BTCUSDT
          </p>
        </div>
        
        <div className="flex flex-col items-end">
          {error ? (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-500/10 text-rose-400 text-sm font-mono border border-rose-500/20 shadow-[0_0_15px_rgba(244,63,94,0.1)]">
              <ShieldAlert className="w-4 h-4" />
              Connection Severed
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-sm font-mono border border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.1)]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Engine Online & Hunting
            </div>
          )}
          <div className="text-[10px] text-gray-500 font-mono mt-2">
            LOCAL SYS_TIME: {localTime.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Top Metrics Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricBox 
          icon={<Wallet />} 
          label="Total Equity" 
          value={`$${fNum(state?.capital)}`} 
          color="cyan" 
          sub="Dynamic Kelly Sizing Active"
        />
        <MetricBox 
          icon={<Activity />} 
          label="Realized PnL" 
          value={`${state?.realized_pnl >= 0 ? '+' : ''}$${fNum(state?.realized_pnl)}`} 
          color={state?.realized_pnl >= 0 ? "emerald" : "rose"} 
          sub={`From ${state?.total_trades || 0} total trades`}
        />
        <MetricBox 
          icon={<Target />} 
          label="Win Rate" 
          value={`${winRate}%`} 
          color="indigo" 
          sub={`${state?.wins || 0}W / ${state?.losses || 0}L`}
        />
        <MetricBox 
          icon={<Globe2 />} 
          label="Network Latency" 
          value={`${fNum(state?.latency_ms, 1)} ms`} 
          color="fuchsia" 
          sub="Binance WebSockets L2"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Core Position Panel */}
        <div className="lg:col-span-2 bg-[#090b10] border border-white/5 rounded-2xl p-6 relative overflow-hidden group hover:border-white/10 transition-colors shadow-2xl">
          <div className={`absolute top-0 left-0 w-1 h-full transition-colors duration-500 ${state?.in_trade ? (state?.trade_dir === 1 ? 'bg-emerald-500 shadow-[0_0_30px_rgba(16,185,129,0.5)]' : 'bg-rose-500 shadow-[0_0_30px_rgba(244,63,94,0.5)]') : 'bg-zinc-800'}`}></div>
          
          <div className="flex justify-between items-center mb-6 pl-4">
            <h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold flex items-center gap-2">
              <Cpu className="w-4 h-4" /> Active Micro-Position
            </h2>
            <div className="flex gap-2">
              {state?.trading_mode === "PAPER" ? (
                <span className="px-2 py-1 rounded bg-amber-500/10 text-amber-500 border border-amber-500/20 text-[10px] font-mono font-bold">PAPER TRADING</span>
              ) : (
                <span className="px-2 py-1 rounded bg-red-500/10 text-red-500 border border-red-500/20 text-[10px] font-mono font-bold animate-pulse">LIVE TRADING</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 pl-4">
            <div>
              <div className="text-[10px] text-gray-500 font-mono mb-2 uppercase">Direction & Size</div>
              {state?.in_trade ? (
                <div className="flex items-end gap-3">
                  <div className={`text-5xl font-black tracking-tighter ${state.trade_dir === 1 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {state.trade_dir === 1 ? 'LONG' : 'SHORT'}
                  </div>
                  <div className="text-gray-400 font-mono mb-1 text-sm">
                    {fNum(state.position_size_usd, 0)} USD
                  </div>
                </div>
              ) : (
                <div className="text-4xl font-black tracking-tighter text-zinc-700">FLAT</div>
              )}
            </div>
            
            <div>
              <div className="text-[10px] text-gray-500 font-mono mb-2 uppercase">Unrealized PnL</div>
              {state?.in_trade ? (
                <div className="flex flex-col">
                  <div className={`text-4xl font-mono font-bold tracking-tight ${pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {pnl >= 0 ? '+' : ''}{fNum(pnlUsd, 2)}
                  </div>
                  <div className={`text-sm font-mono mt-1 ${pnl >= 0 ? 'text-emerald-400/70' : 'text-rose-400/70'}`}>
                    ({pnl >= 0 ? '+' : ''}{(pnl * 100).toFixed(3)}%)
                  </div>
                </div>
              ) : (
                <div className="text-sm font-mono text-gray-600 mt-4">Waiting for XGBoost Alpha Signal...</div>
              )}
            </div>
          </div>

          {state?.in_trade && (
            <div className="grid grid-cols-2 gap-4 mt-8 pl-4">
              <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Entry Price</div>
                <div className="font-mono text-lg text-white">{fNum(state.entry_price, 1)}</div>
              </div>
              <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Current Mark (Mid)</div>
                <div className="font-mono text-lg text-white">{fNum(midPrice, 1)}</div>
              </div>
            </div>
          )}
        </div>

        {/* AI Telemetry */}
        <div className="bg-[#090b10] border border-white/5 rounded-2xl p-6 relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-fuchsia-500/10 blur-[60px] rounded-full"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-500/10 blur-[60px] rounded-full"></div>
          
          <h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold mb-6 flex items-center gap-2 relative z-10">
            <Network className="w-4 h-4 text-fuchsia-400" /> XGBoost Telemetry
          </h2>
          
          <div className="space-y-8 relative z-10">
            <div>
              <div className="flex justify-between items-end mb-2">
                <span className="text-xs text-gray-400 font-mono">Inference Confidence</span>
                <span className="text-lg font-mono font-bold text-white">{(state?.last_confidence * 100).toFixed(2)}%</span>
              </div>
              <div className="h-2.5 w-full bg-black rounded-full overflow-hidden border border-white/10 relative">
                <div 
                  className={`absolute top-0 left-0 h-full transition-all duration-300 ${state?.last_signal === 'BUY' ? 'bg-cyan-400 shadow-[0_0_10px_#22d3ee]' : state?.last_signal === 'SELL' ? 'bg-fuchsia-400 shadow-[0_0_10px_#e879f9]' : 'bg-zinc-600'}`} 
                  style={{ width: `${Math.min(100, Math.max(0, state?.last_confidence * 100))}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[10px] font-mono text-gray-500 uppercase">
                <span>Neutral</span>
                <span className={state?.last_signal === 'BUY' ? 'text-cyan-400 font-bold' : state?.last_signal === 'SELL' ? 'text-fuchsia-400 font-bold' : 'text-gray-400'}>
                  {state?.last_signal || 'FLAT'}
                </span>
                <span>Absolute Conviction</span>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-end mb-2">
                <span className="text-xs text-gray-400 font-mono">Half-Kelly Allocation</span>
                <span className="text-lg font-mono font-bold text-white">{(state?.last_kelly * 100).toFixed(1)}%</span>
              </div>
              <div className="h-2.5 w-full bg-black rounded-full overflow-hidden border border-white/10">
                <div 
                  className="h-full bg-emerald-400 transition-all duration-500 shadow-[0_0_10px_#34d399]" 
                  style={{ width: `${Math.min(100, Math.max(0, state?.last_kelly * 100))}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[10px] font-mono text-gray-500 uppercase">
                <span>Risk Exposure</span>
                <span>Limit: 20% (5x Lev)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Microstructure L2 Viewer */}
      <div className="bg-[#090b10] border border-white/5 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold flex items-center gap-2">
            <Gauge className="w-4 h-4 text-amber-400" /> Orderbook Microstructure (L2)
          </h2>
          <div className="text-[10px] font-mono text-gray-500 border border-white/10 px-2 py-1 rounded bg-black/50">
            Spread: <span className="text-white">{fNum(spread, 2)}</span> USDT
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col items-center justify-center py-8 bg-black/40 rounded-xl border border-cyan-500/10 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-2 z-10">Best Bid (Maker Buy)</span>
            <span className="text-4xl font-mono font-black text-cyan-400 z-10 tracking-tight">{fNum(state?.live_bid, 1)}</span>
          </div>
          <div className="flex flex-col items-center justify-center py-8 bg-black/40 rounded-xl border border-fuchsia-500/10 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-t from-fuchsia-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest mb-2 z-10">Best Ask (Maker Sell)</span>
            <span className="text-4xl font-mono font-black text-fuchsia-400 z-10 tracking-tight">{fNum(state?.live_ask, 1)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricBox({ icon, label, value, color, sub }) {
  const colors = {
    cyan: "text-cyan-400 bg-cyan-400/10 border-cyan-400/20",
    emerald: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
    rose: "text-rose-400 bg-rose-400/10 border-rose-400/20",
    indigo: "text-indigo-400 bg-indigo-400/10 border-indigo-400/20",
    fuchsia: "text-fuchsia-400 bg-fuchsia-400/10 border-fuchsia-400/20",
  };
  
  const c = colors[color] || colors.cyan;

  return (
    <div className="bg-[#090b10] border border-white/5 rounded-2xl p-5 relative overflow-hidden hover:bg-[#0c0f17] transition-colors shadow-lg">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg border ${c}`}>
          {React.cloneElement(icon, { className: "w-4 h-4" })}
        </div>
        <div className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold">{label}</div>
      </div>
      <div className="text-2xl font-mono font-bold text-white tracking-tight">{value}</div>
      <div className="text-[10px] text-gray-500 font-mono mt-2 truncate">{sub}</div>
    </div>
  );
}
